import {Box, Button, Typography} from '@mui/material';
import {t} from 'i18next';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './ReviewerRemarks.scss';
import {ReactComponent as AddIcon} from '../../../../../assets/images/add.svg';

import ArrButton from '../../../../common/arr-button/ArrButton';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  useAddRemark,
  useDeleteRemark,
  useGetRemarks,
} from '../../../../../hooks/document/useDocument';
import {Remark} from '../../../../../api/types/document.type';
import {
  getCurrentDate,
  MouseToolType,
  remarksColors,
} from '../../../docViewerUtils';
import ArrTextArea from '../../../../common/arr-text-area/ArrTextArea';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {convertToUTCDate} from '../../../../../utils/formatters';
import {generateRandomNumber} from '../../../../../utils/utils';

interface ReviewerRemarksProps {
  viewerControl: ViewerControl;
  markForRemark: Mark | null;
  docId: number;
  setCurrentRemarkColor?: Dispatch<SetStateAction<string>>;
  duplicateDocs?: string[];
  annotationLayerId?: string;
  connectionId?: string;
  setRemark: React.Dispatch<React.SetStateAction<Mark | null>>;
  disabled: boolean;
}

const TEXT_LIMIT = 1000;

const RemarksItem = ({
  index,
  result,
  isNew,
  markForRemark,
  viewerControl,
  clearRemark,
  deleteMark,
  duplicateDocs,
  annotationLayerId,
  docId,
  connectionId = '',
  reloadData,
  onDelete,
}: {
  index: number;
  result: Remark;
  isNew: boolean;
  markForRemark?: Mark | null;
  viewerControl?: ViewerControl;
  clearRemark?: () => void;
  deleteMark?: () => void;
  duplicateDocs?: string[];
  annotationLayerId?: string;
  docId?: number;
  connectionId?: string;
  reloadData?: () => void;
  onDelete?: (remark: Remark) => void;
}) => {
  const [remarkText, setRemarkText] = useState('');
  const [editedRemarkText, setEditedRemarkText] = useState('');
  const [editMode, setEditMode] = useState(false);

  const editClickHandler = () => {
    setEditMode(true);
    setEditedRemarkText(result.remarkText);
  };

  const editCancelHandler = () => {
    setEditedRemarkText('');
    setEditMode(false);
  };

  const deleteHandler = () => {
    onDelete?.(result);
  };

  const removeMarkAndClear = () => {
    deleteMark?.();
    clearRemark?.();
  };
  const {
    user: {userId, userName, securityGroupId},
  } = useSelector((state: RootState) => state.authenticatedUser);
  const {mutate} = useAddRemark();

  const editSaveHandler = () => {
    mutate(
      {
        annotationLayerId: annotationLayerId ?? '',
        documentId: docId ?? 0,
        remarkId: Number(result.remarkID),
        remarkText: editedRemarkText,
        remarkColour: result.remarkColor,
      },
      {
        onSuccess: () => {
          editCancelHandler();
          reloadData?.();
        },
      }
    );
  };

  const onAddRemark = useCallback(() => {
    if (markForRemark) {
      const fillColor = markForRemark.fillColor;
      const conversation = markForRemark.getConversation();
      conversation.addComment(remarkText);
      const comment = markForRemark.getConversation().getComments()[0];
      comment.setData('securityGroupId', `${securityGroupId}`);
      comment.setData('userId', `${userId}`);
      comment.setData('userName', userName);
      comment.setData('markId', `${result.remarkID}`);
      comment.setData('remarksColor', fillColor.replace('#', ''));

      mutate(
        {
          annotationLayerId: annotationLayerId ?? '',
          documentId: docId ?? 0,
          remarkId: result.remarkID,
          remarkText: remarkText,
          remarkColour: fillColor.replace('#', ''),
        },
        {
          onSuccess: () => {
            viewerControl?._cs &&
              viewerControl?.saveMarkup(
                `${docId}-${duplicateDocs?.join(',')}-${annotationLayerId}-${connectionId}-NRM`
              );
            clearRemark?.();
          },
          onError: () => {
            removeMarkAndClear?.();
          },
        }
      );
    }
  }, [
    remarkText,
    markForRemark,
    annotationLayerId,
    duplicateDocs,
    connectionId,
    docId,
  ]);
  return (
    <Box
      className="highlight-result"
      sx={{
        borderLeftColor: `#${result.remarkColor.replace('#', '')} !important`,
      }}
    >
      <Typography
        className="typo-text body5_size search-bar-label"
        data-testid={`hit-count-${index}`}
        alignSelf={'flex-end'}
      >
        {isNew ? result.remarkDate : `${convertToUTCDate(result.remarkDate)}`}
      </Typography>
      <Box className="count-wrapper">
        <Typography
          className="typo-text label3_size search-bar-label"
          data-testid={`reviewer-remarks-user-name-${index}`}
        >
          {result.userName}
        </Typography>
      </Box>
      <Box className="text-wrapper">
        {editMode && (
          <ArrTextArea
            value={editedRemarkText}
            onChange={function (newValue: string): void {
              setEditedRemarkText(newValue);
            }}
            maxRows={2}
            className="add-review-remark-text"
            maxLength={TEXT_LIMIT}
            dataTestId={`edit-remark-${index}`}
          />
        )}
        {isNew && !editMode && (
          <ArrTextArea
            value={remarkText}
            onChange={function (newValue: string): void {
              setRemarkText(newValue);
            }}
            maxRows={2}
            className="add-review-remark-text "
            maxLength={TEXT_LIMIT}
            dataTestId="new-remark-input"
          />
        )}
        {!isNew && !editMode && (
          <Typography
            className="typo-text body4_size search-bar-label term-text"
            data-testid={`hit-name-${index}`}
          >
            {result.remarkText}
          </Typography>
        )}
      </Box>
      {isNew && !editMode && (
        <Box className="new-remark-wrapper">
          <ArrButton
            dataTestId={`${index}-cancel`}
            className="previous-button"
            size="small"
            onClick={removeMarkAndClear}
            variant="text"
          >
            {t('docView.reviewRemarks.cancel')}
          </ArrButton>
          <ArrButton
            dataTestId={`${index}-add`}
            className="previous-button"
            disabled={!remarkText}
            size="small"
            onClick={onAddRemark}
            variant="text"
          >
            {t('docView.reviewRemarks.add')}
          </ArrButton>
        </Box>
      )}
      {editMode && (
        <Box className="nav-wrapper">
          <ArrButton
            dataTestId={`${index}-edit-cancel`}
            className="previous-button"
            size="small"
            onClick={editCancelHandler}
            variant="text"
          >
            {t('docView.reviewRemarks.cancel')}
          </ArrButton>
          <ArrButton
            dataTestId={`${index}-edit-save`}
            className="previous-button"
            size="small"
            onClick={editSaveHandler}
            variant="text"
            disabled={
              result.remarkText.trim() == editedRemarkText.trim() ||
              !editedRemarkText.trim()
            }
          >
            {t('docView.reviewRemarks.save')}
          </ArrButton>
        </Box>
      )}
      {!isNew && !editMode && (
        <Box className="nav-wrapper">
          <ArrButton
            dataTestId={`${index}-delete`}
            className="previous-button"
            size="small"
            onClick={deleteHandler}
            variant="text"
          >
            {t('docView.reviewRemarks.delete')}
          </ArrButton>
          <ArrButton
            dataTestId={`${index}-edit`}
            className="previous-button"
            size="small"
            onClick={editClickHandler}
            variant="text"
          >
            {t('docView.reviewRemarks.edit')}
          </ArrButton>
        </Box>
      )}
    </Box>
  );
};

const ReviewerRemarks: React.FC<ReviewerRemarksProps> = ({
  viewerControl,
  markForRemark,
  docId,
  setCurrentRemarkColor,
  annotationLayerId,
  duplicateDocs,
  connectionId,
  setRemark,
  disabled,
}) => {
  const [remarks, setRemarks] = useState<Remark[]>();
  const {
    user: {userId, userName},
  } = useSelector((state: RootState) => state.authenticatedUser);
  const {data: remarkList, refetch} = useGetRemarks(docId);
  const [newRemark, setNewRemark] = useState<Remark>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const remarkToDelete = useRef<Remark>(null);

  const {mutate: deleteRemarkData} = useDeleteRemark(() => {
    deleteDocRemark();
  });

  const deleteDocRemark = () => {
    const allMarks = viewerControl?._cs ? viewerControl.getAllMarks() : [];
    const targetRemark = allMarks.find((mark: any) => {
      const markData = mark.conversation?.getComments()?.[0]?.getData();
      if (
        markData &&
        Number(markData.markId) == remarkToDelete.current?.remarkID
      )
        return true;
      return false;
    });
    if (targetRemark && viewerControl?._cs) {
      viewerControl.deleteMarks([targetRemark]);
      viewerControl?.saveMarkup(
        `${docId}-${duplicateDocs?.join(',')}-${annotationLayerId}-${connectionId}-DRM`
      );
    }
    remarkToDelete.current = null;
    setShowDeleteModal(false);
    refetch();
  };

  const cancelRemarkDelete = () => {
    remarkToDelete.current = null;
    setShowDeleteModal(false);
  };
  const clearRemark = () => {
    if (viewerControl?._cs) {
      viewerControl?.deselectAllMarks();
      setRemark(null);
    }
    setNewRemark(undefined);
    refetch();
    viewerControl &&
      viewerControl?._cs &&
      viewerControl.setCurrentMouseTool(MouseToolType.HighlightAnnotation);
  };

  const deleteMark = () => {
    if (viewerControl?._cs && markForRemark) {
      viewerControl.deleteMarks([markForRemark]);
    }
  };

  useEffect(() => {
    if (remarkList && setCurrentRemarkColor) {
      const colorMap: Record<number, string> = remarkList.reduce<
        Record<number, string>
      >((acc, {userID, remarkColor}) => {
        acc[userID] = remarkColor;
        return acc;
      }, {});

      let foundCurrentUserColor = colorMap[userId];

      if (!foundCurrentUserColor) {
        const exclude = new Set(Object.values(colorMap));
        const remaining = remarksColors.filter((item) => !exclude.has(item));
        // If no remaining values, return any random value from remarksColor
        if (remaining.length === 0) {
          foundCurrentUserColor =
            remarksColors[
              Math.floor(generateRandomNumber() * remarksColors.length)
            ];
        } else {
          // Otherwise return random value from remaining
          foundCurrentUserColor =
            remaining[Math.floor(generateRandomNumber() * remaining.length)];
        }
      }
      foundCurrentUserColor && setCurrentRemarkColor(foundCurrentUserColor);
    }
  }, [remarkList, setCurrentRemarkColor, userId]);

  const onAddRemark = () => {
    if (markForRemark) {
      const fillColor = markForRemark.fillColor;
      const uniqueRemarkId = remarkList?.find(
        (remark) => remark.remarkID === Number(markForRemark.id)
      )
        ? Math.floor(generateRandomNumber() * 9000) + 1000 // generate new remark ID , if prizmdoc generated mark has ID that is already present in remark
        : Number(markForRemark.id);
      setNewRemark({
        remarkID: uniqueRemarkId,
        remarkText: '',
        remarkColor: fillColor.replace('#', ''),
        remarkDate: getCurrentDate(),
        documentID: docId,
        userID: userId,
        userName: userName,
      });
    }
  };

  useEffect(() => {
    if (remarkList) {
      setRemarks(remarkList);
    }
  }, [remarkList, setRemarks]);

  const remarksSorted = useMemo(() => {
    return remarks?.sort((a, b) => {
      return (
        new Date(b.remarkDate).getTime() - new Date(a.remarkDate).getTime()
      );
    });
  }, [remarks]);

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        data-testid="persistent-navigation-wrapper"
        className="reviewer-remarks-wrapper"
      >
        <Box className="search-hits-header">
          <Typography
            className="typo-text subhead5_size title"
            data-testid={`reviewer-remarks-title`}
          >
            {t('docView.reviewRemarks.title')}
          </Typography>
        </Box>
        <Box className="hits-box">
          <Button
            disableRipple
            className={`arr-new-remark-button arr-new-remark-button-inactive`}
            data-testid="add-remark"
            onClick={onAddRemark}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
            disabled={!markForRemark || !!newRemark || disabled}
          >
            <AddIcon width={8} />
            <Typography
              className="typo-text buttonXS_size"
              sx={{marginLeft: '8px'}}
              data-testid={`reviewer-remarks-new`}
            >
              {t('docView.reviewRemarks.new')}
            </Typography>
          </Button>
        </Box>
        {newRemark && (
          <RemarksItem
            index={-1}
            key={-1}
            result={newRemark}
            isNew
            markForRemark={markForRemark}
            viewerControl={viewerControl}
            clearRemark={clearRemark}
            deleteMark={deleteMark}
            duplicateDocs={duplicateDocs}
            annotationLayerId={annotationLayerId}
            connectionId={connectionId}
            docId={docId}
          ></RemarksItem>
        )}

        {remarksSorted &&
          remarksSorted.map((term, index) => (
            <RemarksItem
              index={index}
              key={term.remarkID}
              result={term}
              isNew={false}
              docId={docId}
              annotationLayerId={annotationLayerId}
              reloadData={() => {
                refetch();
              }}
              onDelete={(remark: Remark) => {
                remarkToDelete.current = remark;
                setShowDeleteModal(true);
              }}
            />
          ))}
      </Box>
      {showDeleteModal && (
        <ArrModal
          onClose={cancelRemarkDelete}
          showModal={showDeleteModal}
          title={t('docView.reviewRemarks.deleteModal.title')}
          description={t('docView.reviewRemarks.deleteModal.description')}
          dataTestId="show-delete-modal"
          buttons={[
            {
              id: 'delete-remark-no',
              label: t('docView.reviewRemarks.deleteModal.noButton'),
              onClick: cancelRemarkDelete,
              variant: 'secondary',
              dataTestId: 'delete-remark-no-test',
            },
            {
              id: 'delete-remark-yes',
              label: t('docView.reviewRemarks.deleteModal.yesButton'),
              onClick: () => {
                deleteRemarkData({
                  annotationLayerId: annotationLayerId ?? '',
                  documentId: docId,
                  remarkId: remarkToDelete.current?.remarkID ?? 0,
                });
              },
              variant: 'primary',
              dataTestId: 'delete-remark-yes-test',
            },
          ]}
        />
      )}
    </>
  );
};

function convertTo12HourFormat(dateTimeString: string): string {
  const date = new Date(dateTimeString);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string');
  }

  // Extract date parts (UTC to match input)
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();

  // Extract time (UTC)
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  // Convert to 12-hour format
  const ampm = hours >= 12 ? t('common.pm') : t('common.am');
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, '0');

  return `${month}/${day}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
}

export default ReviewerRemarks;
